import { useQuery } from '@tanstack/react-query';

import { getReadWorkspaceFileContentUrl } from '@/lib/api/generated/api';
import { getAccessToken, renewAccessToken } from '@/lib/auth/tokenStore';
import { env } from '@/lib/env';
import { fileKeys } from '../queryKeys';
import type { Previewability } from '../types';

/**
 * Constitution 2.2 exception, scoped exactly as plan.md's Complexity
 * Tracking table describes: the backend's content endpoint streams arbitrary
 * bytes with a dynamic `Content-Type` that OpenAPI cannot describe, so the
 * generated client types its body as `unknown`. A raw `fetch` read as text is
 * the only way to get a typed string out of it. Do not extend this pattern to
 * any other endpoint — every other hook in this feature (and the app) goes
 * through the generated client.
 *
 * Never fetched for `binary` files (FR-F002/research.md R1 and R2): a binary
 * file is only ever offered as a download link, so no bytes are pulled into
 * the page before the user asks for them.
 */
/** One authenticated read of the content stream, with whatever token is current. */
function send(url: string): Promise<Response> {
  const token = getAccessToken();
  return fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
}

export function useFilePreviewContent(
  workspaceId: string,
  path: string,
  previewability: Previewability,
) {
  const enabled =
    previewability.kind === 'markdown' ||
    previewability.kind === 'code' ||
    previewability.kind === 'text';

  const query = useQuery({
    queryKey: fileKeys.content(workspaceId, path),
    queryFn: async () => {
      const url = `${env.VITE_API_BASE_URL}${getReadWorkspaceFileContentUrl(workspaceId, { path })}`;
      // A raw `fetch`, not `customFetch`, because this streams a file body rather
      // than JSON — so everything `customFetch` does for a token has to be done
      // here too. The store is the shared source both paths read (spec 067).
      //
      // Read inside the query function, never captured at render: a token fetched
      // when the component rendered may already have expired by the time someone
      // opens a preview.
      let response = await send(url);
      // Renew once and replay, exactly as `client.ts` does. Without it an expired
      // token made a preview fail permanently — `retry: false` below is correct for
      // a genuine read failure, but it also blocked the one retry that would have
      // worked, while every other request in the app renewed transparently.
      if (response.status === 401 && getAccessToken()) {
        const renewed = await renewAccessToken();
        if (renewed) response = await send(url);
      }
      if (!response.ok) {
        throw new Error('This file could not be loaded.');
      }
      return response.text();
    },
    enabled,
    retry: false,
  });

  return {
    content: query.data,
    isPending: enabled && query.isPending,
    loadFailed: enabled && !query.isPending && query.isError,
    refetch: query.refetch,
  };
}
